<template>
    <div
        ref="containerRef"
        class="c-sticky-panels"
        :class="dynamicClass"
        :style="`--sticky-header-offset: ${stickyHeaderOffset}px`"
    >
        <div ref="bgPanelTopRef" class="c-sticky-panels__bg-top"></div>
        <DsBlock class="c-sticky-panels__bg">
            <div
                v-for="(color, index) in bgColours"
                :key="`${color}-${index}`"
                class="c-sticky-panels__bg-layer"
                :class="{ 'is-visible': index === activePanelIndex }"
                :style="{ backgroundColor: `var(--ds-color-bg-${color})` }"
            ></div>
            <div v-if="!isMobile" class="c-sticky-panels__media">
                <StickyMedia
                    v-for="(panel, index) in panels"
                    :key="panel.image"
                    :is-active="activePanelIndex === index"
                    :image-alt="panel.imageAlt"
                    :image-src="panel.image"
                    :stacking-index="panels.length - index"
                    :media-focal-point="panel.mediaFocalPoint"
                >
                </StickyMedia>
            </div>
        </DsBlock>
        <StickyPanel
            v-for="(panel, index) in panels"
            :key="panel.title"
            :ref="(el) => registerPanelRef(el, index)"
            :active-index="activePanelIndex"
            :active-bg-colour="activeBgColour"
            :bg-colour="bgColours[index % bgColours.length]"
            :facts="panel.facts"
            :inline-media="isMobile"
            :link-is-external="panel.linkIsExternal"
            :link-text="panel.linkText"
            :link-url="panel.linkUrl"
            :media-focal-point="panel.mediaFocalPoint"
            :tag="panel.tag"
            :title="panel.title"
            :text="panel.text"
            :image="panel.image"
            :image-alt="panel.imageAlt"
            :index="index"
            :number-of-panels="panels.length"
            :class="{ 'is-above': index < activePanelIndex }"
        ></StickyPanel>
        <div ref="bgPanelBottomRef" class="c-sticky-panels__bg-bottom"></div>
    </div>
</template>

<script setup lang="ts">
import {
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
    type ComponentPublicInstance,
    watch,
    nextTick,
} from "vue";
import { useIntersectionObserver, useScroll } from "@vueuse/core";
import { type StickyPanel as StickyPanelType } from "./sticky-panel.vue";
import { DsBlock, dsUseIsMobile } from "@coloplast/design-system";
import StickyPanel from "./sticky-panel.vue";
import StickyMedia from "./sticky-media.vue";
import "./sticky-panels.scss";

export type StickyPanelProps = {
    /** Array of panels */
    panels?: Array<StickyPanelType>;
    /** Scroll snapping behaviour */
    scrollSnap?: "mandatory" | "proximity" | "none";
    /** Number of pixels to offset for any sticky header elements */
    stickyHeaderOffset?: number;
};

// Constants
const LOW_THRESHOLD = 0.1;
const HIGH_THRESHOLD = 0.8;

// Props
const props = withDefaults(defineProps<StickyPanelProps>(), {
    panels: () => [],
    scrollSnap: "mandatory",
    stickyHeaderOffset: 0,
});

// Composables
const { isMobile } = dsUseIsMobile();
const { isMobile: isMobileOrTablet } = dsUseIsMobile(1200);

// Refs
const containerRef = ref<HTMLElement | null>(null);
const bgPanelTopRef = ref<HTMLElement | null>(null);
const bgPanelBottomRef = ref<HTMLElement | null>(null);
const panelRefs = ref<Array<HTMLElement | null>>([]);
const activePanelIndex = ref(0);
const topOut = ref(false);
const bottomOut = ref(false);
const containerIsIntersecting = ref(false);
const isStuck = computed(
    () => topOut.value && bottomOut.value && containerIsIntersecting.value,
);

const { directions } = useScroll(window);

// Computed
const bgColours: Array<
    "inverse" | "decorative-2-subtle" | "decorative-3-muted"
> = ["inverse", "decorative-2-subtle", "decorative-3-muted"];
const activeBgColour = computed(() => {
    return bgColours[activePanelIndex.value % bgColours.length];
});

const shouldScrollSnappingBeActive = computed(() => {
    if (!directions.bottom && activePanelIndex.value === 0) return false;
    if (
        !directions.top &&
        activePanelIndex.value === panelRefs.value.length - 1
    )
        return false;
    return isStuck.value;
});

const dynamicClass = computed(() => {
    const classNames = [`c-sticky-panels--bg-${activeBgColour.value}`];
    if (shouldScrollSnappingBeActive.value && !isMobileOrTablet.value) {
        classNames.push("c-sticky-panels--active");
    }
    if (props.scrollSnap && !isMobileOrTablet.value) {
        classNames.push(`c-sticky-panels--scroll-snap-${props.scrollSnap}`);
    }
    return classNames;
});

const registerPanelRef = (
    el: Element | ComponentPublicInstance | null | undefined,
    index: number,
) => {
    const domEl = (el as ComponentPublicInstance)?.$el ?? el;
    panelRefs.value[index] = domEl instanceof HTMLElement ? domEl : null;
};

const getVisibleRatio = (el: HTMLElement): number => {
    const rect = el.getBoundingClientRect();
    if (rect.height <= 0) return 0;
    const visible =
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    return Math.max(0, visible / rect.height);
};

const getBestVisiblePanel = (): {
    index: number;
    panel: HTMLElement | null;
} => {
    return panelRefs.value.reduce(
        (best, panel, i) => {
            if (!panel) return best;
            const ratio = getVisibleRatio(panel);
            return ratio >= LOW_THRESHOLD && ratio > best.ratio
                ? { ratio, index: i, panel }
                : best;
        },
        {
            ratio: 0,
            index: activePanelIndex.value,
            panel: null as HTMLElement | null,
        },
    );
};

const setCorrectPanelOnDesktop = () => {
    const { index, panel } = getBestVisiblePanel();
    if (index !== activePanelIndex.value) activePanelIndex.value = index;
    panel?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const mediaQuery = globalThis.matchMedia("(min-width: 1200px)");
const handleMediaChange = (e: MediaQueryListEvent) => {
    if (e.matches) {
        setCorrectPanelOnDesktop();
    }
};

onMounted(() => {
    if (bgPanelTopRef.value) {
        useIntersectionObserver(
            bgPanelTopRef,
            ([entry]) => {
                if (!entry) return;
                topOut.value = !entry.isIntersecting;
            },
            { threshold: [0] },
        );
    }
    if (bgPanelBottomRef.value) {
        useIntersectionObserver(
            bgPanelBottomRef,
            ([entry]) => {
                if (!entry) return;
                bottomOut.value = !entry.isIntersecting;
            },
            { threshold: [0] },
        );
    }
    if (containerRef.value) {
        useIntersectionObserver(
            containerRef,
            ([entry]) => {
                if (!entry) return;
                containerIsIntersecting.value = entry.isIntersecting;
            },
            { threshold: [0] },
        );
    }
    panelRefs.value.forEach((panel, index) => {
        if (!panel) return;
        useIntersectionObserver(
            panel,
            ([entry]) => {
                if (!isStuck.value) return;
                if (!entry) return;

                if (entry.isIntersecting) {
                    activePanelIndex.value = index;
                }
            },
            { threshold: HIGH_THRESHOLD },
        );
    });

    mediaQuery.addEventListener("change", handleMediaChange);
});

onBeforeUnmount(() => {
    mediaQuery.removeEventListener("change", handleMediaChange);
});

watch(isMobileOrTablet, async (newValue) => {
    if (newValue === false) {
        await nextTick(() => {
            setCorrectPanelOnDesktop();
        });
    }
});

watch(shouldScrollSnappingBeActive, () => {
    // Need to ensure that when we deactivate scroll snapping that the block is reset
    if (topOut.value === false && bottomOut.value === true) {
        activePanelIndex.value = 0;
    }
    if (bottomOut.value === false && topOut.value === true) {
        activePanelIndex.value = panelRefs.value.length - 1;
    }
});
</script>
